import React, { useEffect } from "react";

interface ToastMessageProps {
  message: string;
  type?: "info" | "warning" | "error";
  duration?: number;
  onClose: () => void;
}

export const ToastMessage: React.FC<ToastMessageProps> = ({
  message,
  type = "info",
  duration = 5000,
  onClose,
}) => {
  useEffect(() => {
    if (duration > 0) {
      const timeoutId: number = setTimeout((): void => onClose(), duration);
      return (): void => clearTimeout(timeoutId);
    }
  }, [duration, onClose]);

  function getAriaLabel(): string {
    switch (type) {
      case "error":
        return "Error notification";
      case "warning":
        return "Warning notification";
      default:
        return "Information notification";
    }
  }

  return (
    <div
      aria-atomic="true"
      aria-label={getAriaLabel()}
      className={`error-toast error-toast-${type}`}
      role={type === "error" || type === "warning" ? "alert" : "status"}
      aria-live={
        type === "error" || type === "warning" ? "assertive" : "polite"
      }
    >
      <div className="error-toast-content">
        <div className="error-toast-icon" aria-hidden="true">
          {(type === "error" || type === "warning") && "⚠️"}
          {type === "info" && "ℹ️"}
        </div>
        <div className="error-toast-message">{message}</div>
        <button
          className="error-toast-close"
          onClick={onClose}
          aria-label={`Close ${type} notification`}
        >
          <span aria-hidden="true">×</span>
          <span className="sr-only">Close</span>
        </button>
      </div>
    </div>
  );
};
